import { pipe, prop, equals } from 'rambda';
import { TYPES } from '../flattenTree/utils/consts';
import {
  isLegacyType,
  LegacyComplexType,
  LegacySingleType,
  Type,
  TypeParameter,
} from '../../client/components/TypeDetails/typeParser';
import { Operation } from '../../types';
import { extractType } from '../../client/components/TypeDetails/utils';
import { hasMembers } from '../../client/components/TypeDetails/OperationDetails/utils';
import { getMembers } from '../../client/components/TypeDetails/NestedTable/utils';
import { isLink } from '../../client/components/TypeDetails/NestedTable/NestedTableNameDisplay/utils';

export const NODE_TYPES = {
  TAG: 'TAG',
};

export const isObject = (obj) =>
  obj !== null && typeof obj === 'object' && !Array.isArray(obj);

export const isTagNode = pipe(prop('type'), equals(NODE_TYPES.TAG));

export const isArticlesCategory = (node) =>
  node?.data?.propType === TYPES.ARTICLES_CATEGORY;

export const isArticlesSectionGuide = ({ data, type }) =>
  type && type === TYPES.MD && data?.extra?.isArticlesSectionGuide;

export const getTypeSuffix = (type: string = ''): string =>
  type.split('.').pop();

export const getCallback = (
  type: Type | LegacySingleType,
  store,
): Operation => {
  /**
   * If `type` is an array, we look at the first possible type.
   * We currently assume that a callback parameter is not a part of a union type (e.g `BlaCallback | BlaBlaCallback`.
   * Maybe in the future we will need to support having multiple callback types in the parameter.
   */
  const extractMemberFromNodeCallbacks = ({ currentNode, memberName }) => {
    if (currentNode && currentNode.data && currentNode.data.callbacks) {
      return currentNode.data.callbacks.find((cb) => cb.name === memberName);
    }
  };

  const nodeCallbacks = store.findMembersByType(
    extractType(Array.isArray(type) ? type[0] : type),
  );

  return extractMemberFromNodeCallbacks(nodeCallbacks);
};

export const getReferencesToMessagesInType = (
  types: Type[] | LegacySingleType[] | TypeParameter[],
  store,
): (string | LegacyComplexType)[] => {
  const hasSingleTypeGotMembers = (singleType) =>
    hasMembers(getMembers(store)(singleType)) &&
    !isLink(store.docsTree, singleType); // we do not want to include references to operations/sections

  const arrayReferences = [];

  types.forEach((type) => {
    if (isLegacyType(type) && hasSingleTypeGotMembers(type)) {
      arrayReferences.push(type);
    } else if (
      type.referenceType &&
      hasSingleTypeGotMembers(type.referenceType)
    ) {
      arrayReferences.push(type.referenceType);
    } else if (type.complexType) {
      if (
        type.complexType.referenceType &&
        hasSingleTypeGotMembers(type.complexType.referenceType)
      ) {
        arrayReferences.push(type.complexType.referenceType);
      }
      const referencesInTypeParams = getReferencesToMessagesInType(
        type.complexType.typeParams,
        store,
      );
      arrayReferences.push(...referencesInTypeParams);
    } else if (type.unionType) {
      const referencesInUnionType = getReferencesToMessagesInType(
        type.unionType.type,
        store,
      );
      arrayReferences.push(...referencesInUnionType);
    }
  });

  return arrayReferences;
};
